import { useEffect, useState, useCallback } from "react";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAdminApi } from "../hooks/useAdminApi";

type MintStatus = "pending" | "minted" | "failed" | "unconfirmed";

interface BadgeNFT {
    id: string;
    badge_id: string;
    badge_name: string;
    badge_category: string;
    badge_tier: string;
    user_id: number;
    owner_address: string;
    nft_index: number;
    nft_address: string;
    mint_status: MintStatus;
    mint_tx_hash: string;
    image_url: string;
    created_at: any;
}

interface Stats {
    total: number;
    minted: number;
    pending: number;
    failed: number;
    unconfirmed: number;
    images: number;
    definitions: number;
}

const STATUS_CONFIG: Record<MintStatus, { label: string; color: string; bgColor: string; borderColor: string }> = {
    minted: { label: "✅ Minted", color: "text-emerald-400", bgColor: "bg-emerald-500/10", borderColor: "border-emerald-500/20" },
    pending: { label: "⏳ Pending", color: "text-yellow-400", bgColor: "bg-yellow-500/10", borderColor: "border-yellow-500/20" },
    failed: { label: "❌ Failed", color: "text-red-400", bgColor: "bg-red-500/10", borderColor: "border-red-500/20" },
    unconfirmed: { label: "⚠️ Unconfirmed", color: "text-orange-400", bgColor: "bg-orange-500/10", borderColor: "border-orange-500/20" },
};

export function NFTManagementView() {
    const [stats, setStats] = useState<Stats>({ total: 0, minted: 0, pending: 0, failed: 0, unconfirmed: 0, images: 0, definitions: 0 });
    const [failedNFTs, setFailedNFTs] = useState<BadgeNFT[]>([]);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState<string | null>(null);
    const [retryingAll, setRetryingAll] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [retryResult, setRetryResult] = useState<{ type: "success" | "error"; message: string } | null>(null);
    const navigate = useNavigate();
    const firestore = getFirestore();
    const { fetchApi } = useAdminApi();

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const nftsSnap = await getDocs(collection(firestore, "badge_nfts"));
            const imagesSnap = await getDocs(collection(firestore, "badge_images"));
            const defsSnap = await getDocs(collection(firestore, "badge_definitions"));

            let minted = 0, pending = 0, failed = 0, unconfirmed = 0;
            const failedList: BadgeNFT[] = [];

            nftsSnap.docs.forEach(doc => {
                const data = doc.data();
                const status = data.mint_status as MintStatus;
                if (status === "minted") minted++;
                else if (status === "pending") pending++;
                else if (status === "failed") { failed++; failedList.push({ id: doc.id, ...data } as BadgeNFT); }
                else if (status === "unconfirmed") { unconfirmed++; failedList.push({ id: doc.id, ...data } as BadgeNFT); }
            });

            setStats({ total: nftsSnap.size, minted, pending, failed, unconfirmed, images: imagesSnap.size, definitions: defsSnap.size });
            setFailedNFTs(failedList);
        } catch (error) {
            console.error("Error loading NFT data:", error);
        } finally {
            setLoading(false);
        }
    }, [firestore]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleRetry = async (nftId: string) => {
        setRetrying(nftId);
        setRetryResult(null);
        try {
            const result = await fetchApi(`/admin/api/badge-nfts/${nftId}/retry`, { method: "POST" });
            setRetryResult({ type: "success", message: `Mint retried → ${result.nft_address || "pending"}` });
            await loadData();
        } catch (error: any) {
            setRetryResult({ type: "error", message: error.message || "Retry failed" });
        } finally {
            setRetrying(null);
        }
    };

    const handleRetryAll = async () => {
        setRetryingAll(true);
        setRetryResult(null);
        try {
            const result = await fetchApi("/admin/api/badge-nfts/retry-all-failed", { method: "POST" });
            setRetryResult({
                type: result.failed === 0 ? "success" : "error",
                message: `Batch retry: ${result.succeeded}/${result.total} succeeded, ${result.failed} failed`,
            });
            await loadData();
        } catch (error: any) {
            setRetryResult({ type: "error", message: error.message || "Batch retry failed" });
        } finally {
            setRetryingAll(false);
        }
    };

    const shortenAddress = (addr: string) =>
        addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "—";

    const handleSeedDefaults = async () => {
        setSeeding(true);
        setRetryResult(null);
        try {
            const result = await fetchApi("/admin/api/badge-definitions/seed", { method: "POST" });
            setRetryResult({
                type: "success",
                message: `Seed complete: ${result.created} created, ${result.skipped} already existed`,
            });
            await loadData();
        } catch (error: any) {
            setRetryResult({ type: "error", message: error.message || "Seed failed" });
        } finally {
            setSeeding(false);
        }
    };

    return (
        <div className="p-8 max-w-6xl mx-auto space-y-10 min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <h1 className="text-4xl font-black text-white tracking-tight uppercase">NFT Management</h1>
                    <p className="text-slate-500 mt-2 text-lg">Monitor badge NFT minting, retry failures, and manage badges.</p>
                </div>
                <div className="flex gap-3 self-start md:self-auto flex-wrap">
                    {stats.definitions === 0 && !loading && (
                        <button
                            onClick={handleSeedDefaults}
                            disabled={seeding}
                            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold rounded-2xl transition-all flex items-center gap-2"
                        >
                            {seeding ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Seeding…
                                </>
                            ) : (
                                <>🌱 Seed Default Badges</>
                            )}
                        </button>
                    )}
                    {(stats.failed > 0 || stats.unconfirmed > 0) && (
                        <button
                            onClick={handleRetryAll}
                            disabled={retryingAll}
                            className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold rounded-2xl transition-all flex items-center gap-2"
                        >
                            {retryingAll ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Retrying…
                                </>
                            ) : (
                                <>🔄 Retry All Failed ({stats.failed + stats.unconfirmed})</>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Notification */}
            {retryResult && (
                <div className={`p-4 rounded-2xl border ${retryResult.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" : "bg-red-500/10 border-red-500/20 text-red-400"}`}>
                    {retryResult.message}
                    <button onClick={() => setRetryResult(null)} className="ml-4 opacity-50 hover:opacity-100">✕</button>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                    { label: "Total NFTs", value: stats.total, color: "border-b-blue-500" },
                    { label: "Minted", value: stats.minted, color: "border-b-emerald-500" },
                    { label: "Pending", value: stats.pending, color: "border-b-yellow-500" },
                    { label: "Failed", value: stats.failed, color: "border-b-red-500" },
                    { label: "Unconfirmed", value: stats.unconfirmed, color: "border-b-orange-500" },
                    { label: "Definitions", value: stats.definitions, color: "border-b-indigo-500" },
                    { label: "Badge Images", value: stats.images, color: "border-b-purple-500" },
                ].map((s) => (
                    <div key={s.label} className={`bg-slate-900 border border-slate-800 rounded-2xl p-4 border-b-4 ${s.color}`}>
                        <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">{s.label}</p>
                        <h2 className="text-2xl font-black text-white mt-1">
                            {loading ? <div className="h-7 w-10 bg-slate-800 rounded animate-pulse mt-1" /> : s.value}
                        </h2>
                    </div>
                ))}
            </div>

            {/* Quick Navigation */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                    { title: "Badge Definitions", desc: "Create and edit badge types", icon: "🏆", path: "/badge-definitions", color: "bg-indigo-500" },
                    { title: "Badge Images", desc: "Upload and manage NFT artwork", icon: "🖼️", path: "/c/badge_images", color: "bg-purple-500" },
                    { title: "Badge NFTs", desc: "View all minted badge NFTs", icon: "🏅", path: "/c/badge_nfts", color: "bg-blue-500" },
                    { title: "Issued Coupons", desc: "Manage issued coupons", icon: "🎟️", path: "/c/issued_coupons", color: "bg-amber-500" },
                ].map((action) => (
                    <button
                        key={action.path}
                        onClick={() => navigate(action.path)}
                        className="flex items-center gap-4 p-5 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-2xl transition-all group text-left"
                    >
                        <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform shadow-lg shadow-black/20`}>
                            {action.icon}
                        </div>
                        <div className="flex-1">
                            <p className="text-white font-bold">{action.title}</p>
                            <p className="text-slate-500 text-xs">{action.desc}</p>
                        </div>
                        <svg className="w-5 h-5 text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                ))}
            </div>

            {/* Failed / Unconfirmed NFTs Table */}
            <div className="space-y-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <span className="w-1.5 h-6 bg-red-500 rounded-full"></span>
                    Failed &amp; Unconfirmed Mints
                </h3>

                <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-950/50">
                                <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Badge</th>
                                <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">User</th>
                                <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Wallet</th>
                                <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Status</th>
                                <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array(3).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-24" /></td>
                                        <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-16" /></td>
                                        <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-24" /></td>
                                        <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-20" /></td>
                                        <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-16" /></td>
                                    </tr>
                                ))
                            ) : failedNFTs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p-10 text-center text-slate-500">
                                        🎉 No failed or unconfirmed mints — everything looks good!
                                    </td>
                                </tr>
                            ) : (
                                failedNFTs.map((nft) => {
                                    const sc = STATUS_CONFIG[nft.mint_status] || STATUS_CONFIG.failed;
                                    return (
                                        <tr key={nft.id} className="hover:bg-slate-800/30 transition-colors">
                                            <td className="p-4 border-b border-slate-800/50">
                                                <div>
                                                    <p className="text-white font-medium text-sm">{nft.badge_name || nft.badge_id}</p>
                                                    <p className="text-slate-500 text-[10px]">{nft.badge_category} · {nft.badge_tier}</p>
                                                </div>
                                            </td>
                                            <td className="p-4 border-b border-slate-800/50 text-slate-300 text-sm font-mono">
                                                {nft.user_id}
                                            </td>
                                            <td className="p-4 border-b border-slate-800/50 text-slate-400 text-sm font-mono">
                                                {shortenAddress(nft.owner_address)}
                                            </td>
                                            <td className="p-4 border-b border-slate-800/50">
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${sc.bgColor} ${sc.color} border ${sc.borderColor}`}>
                                                    {sc.label}
                                                </span>
                                            </td>
                                            <td className="p-4 border-b border-slate-800/50">
                                                <button
                                                    onClick={() => handleRetry(nft.id)}
                                                    disabled={retrying === nft.id}
                                                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white text-xs font-bold rounded-lg transition-all"
                                                >
                                                    {retrying === nft.id ? "Retrying…" : "Retry Mint"}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
